/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

export default class PreferenceService {
    private static dbName = "UserPreferencesDB";
    private static storeName = "preferences";
    private static dbInstance: IDBDatabase | null = null;
  
    private static async initDB(): Promise<IDBDatabase> {
      if (this.dbInstance) return this.dbInstance;
  
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(this.dbName, 1);
  
        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains(this.storeName)) {
            db.createObjectStore(this.storeName);
          }
        };
  
        request.onsuccess = () => {
          this.dbInstance = request.result;
          resolve(this.dbInstance);
        };
  
        request.onerror = () => reject(request.error);
      });
    }
  
    public static async getUserPreference(prefName: string): Promise<string | null> {
      try {
        const db = await this.initDB();
        return new Promise((resolve, reject) => {
          const transaction = db.transaction(this.storeName, "readonly");
          const store = transaction.objectStore(this.storeName);
          const request = store.get(prefName);
  
          request.onsuccess = () => resolve(request.result || null);
          request.onerror = () => reject(request.error);
        });
      } catch (error) {
        console.error(`Error fetching preference '${prefName}':`, error);
        return null;
      }
    }
  
    public static async setUserPreference(prefName: string, prefValue: string | null): Promise<void> {
      try {
        const db = await this.initDB();
        return new Promise((resolve, reject) => {
          const transaction = db.transaction(this.storeName, "readwrite");
          const store = transaction.objectStore(this.storeName);
          const request = store.put(prefValue, prefName);
  
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      } catch (error) {
        console.error(`Error saving preference '${prefName}':`, error);
      }
    }
  }
  